import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type ThemePreference = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
  theme: ResolvedTheme;
  themePreference: ThemePreference;
  toggleTheme: () => void;
  setThemePreference: (value: ThemePreference) => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'dark',
  themePreference: 'dark',
  toggleTheme: () => {},
  setThemePreference: () => {},
  isDark: true,
});

function resolveTheme(themePreference: ThemePreference, prefersDark: boolean): ResolvedTheme {
  if (themePreference === 'system') {
    return prefersDark ? 'dark' : 'light';
  }
  return themePreference;
}

export function ThemeProvider({ children, preference }: { children: React.ReactNode; preference?: ThemePreference }) {
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(() => {
    try {
      const stored = localStorage.getItem('forxa-theme-preference');
      if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
    } catch { /* Storage may be blocked; keep the preference in memory. */ }
    return preference || 'dark';
  });
  const [theme, setTheme] = useState<ResolvedTheme>('dark');

  useEffect(() => {
    if (preference && ['light', 'dark', 'system'].includes(preference)) {
      setThemePreferenceState(preference);
    }
  }, [preference]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');

    const applyTheme = () => {
      const nextTheme = resolveTheme(themePreference, media.matches);
      setTheme(nextTheme);
      const root = document.documentElement;
      if (nextTheme === 'dark') root.classList.add('dark');
      else root.classList.remove('dark');
    };

    applyTheme();
    try { localStorage.setItem('forxa-theme-preference', themePreference); } catch { /* Keep the in-memory preference. */ }
    media.addEventListener('change', applyTheme);
    return () => media.removeEventListener('change', applyTheme);
  }, [themePreference]);

  useEffect(() => {
    let stored: string | null = null;
    try { stored = localStorage.getItem('forxa-theme-preference'); } catch { /* Use the supplied preference or default. */ }
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const initialPreference: ThemePreference =
      stored === 'light' || stored === 'dark' || stored === 'system'
        ? stored
        : preference || 'dark';
    const nextTheme = resolveTheme(initialPreference, media.matches);
    const root = document.documentElement;
    if (nextTheme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
  }, [preference]);

  const toggleTheme = useCallback(() => {
    const root = document.documentElement;
    const basis = themePreference === 'system' ? theme : themePreference;
    const nextPref: ThemePreference = basis === 'dark' ? 'light' : 'dark';

    // Add transition class BEFORE the theme class changes so the browser
    // has transitions enabled when the color properties actually flip.
    root.classList.add('theme-switching');
    if (nextPref === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');

    setThemePreferenceState(nextPref);

    // Remove after transitions finish (must be ≥ the CSS duration below)
    setTimeout(() => root.classList.remove('theme-switching'), 350);
  }, [themePreference, theme]);

  const setThemePreference = useCallback((value: ThemePreference) => {
    setThemePreferenceState(value);
  }, []);

  const value = useMemo<ThemeContextValue>(() => ({
    theme,
    themePreference,
    toggleTheme,
    setThemePreference,
    isDark: theme === 'dark',
  }), [theme, themePreference, toggleTheme, setThemePreference]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
